import {
    AbstractControl,
    AsyncValidatorFn,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import {DateTime} from 'luxon';
import {map, Observable, of, tap} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {HttpErrorResponse} from '@angular/common/http';

// Copied from Angular source code and modified to require a top domain of at least 2 characters (a-zA-Z0-9).
export const EMAIL_REGEXP =
    /^(?=.{1,254}$)(?=.{1,64}@)[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.([a-zA-Z0-9]){2,}$/;

const HOUR_REGEXP = /^(2[0-3]|[01]?[0-9])$/;
const MINUTE_REGEXP = /^[0-5]?[0-9]$/;

// RFC 5321/5322 practical maximum email address length (254, not the theoretical 320).
export const EMAIL_MAX_LENGTH = 254;

// SAP's business-partner email field (ADR6-SMTP_ADDR) is limited to 241 characters.
// The SAP sync sends the billing-address email untruncated, so emails destined for SAP
// must stay within this limit to avoid a failed business-partner sync.
export const SAP_EMAIL_MAX_LENGTH = 241;

export class AppValidators {
    static hour(): ValidatorFn {
        return Validators.pattern(HOUR_REGEXP);
    }

    static minute(): ValidatorFn {
        return Validators.pattern(MINUTE_REGEXP);
    }

    // Copied from Angular source code
    static email(control: AbstractControl): ValidationErrors | null {
        if (AppValidators.isEmptyInputValue(control.value) || control.pristine) {
            return null; // don't validate empty or unchanged values to allow optional controls
        }
        return EMAIL_REGEXP.test(control.value) ? null : {email: true};
    }

    // Unlike `email`, this also validates a value the form was seeded with, so an invalid stored
    // address is flagged before the operator touches the field.
    static emailWhenPresent(control: AbstractControl): ValidationErrors | null {
        if (AppValidators.isEmptyInputValue(control.value)) {
            return null;
        }
        return EMAIL_REGEXP.test(control.value) ? null : {email: true};
    }

    static postalCode(
        getCityFromCountryAndPostalCode: (
            country: string,
            postalCode: string,
        ) => Observable<string | null>,
        setCity: (city: string | null) => void,
        required: boolean,
    ): AsyncValidatorFn {
        return (
            control: AbstractControl,
        ): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> => {
            setCity('');

            const country: unknown = control.get('country')?.value;
            const postalCode: unknown = control.get('postalCode')?.value;

            const oneValueIsMissing =
                typeof country !== 'string' ||
                country.length === 0 ||
                typeof postalCode !== 'string' ||
                postalCode.length === 0;
            const oneValueIsPresent =
                (typeof country === 'string' && country.length > 0) ||
                (typeof postalCode === 'string' && postalCode.length > 0);
            const noValuesArePresent =
                (typeof country !== 'string' || country.length === 0) &&
                (typeof postalCode !== 'string' || postalCode.length === 0);

            if (required && oneValueIsMissing) {
                // Values are missing
                return of({required: true});
            }

            if (!required && noValuesArePresent) {
                // No values are entered and a value is not required. Then there is no error.
                return of(null);
            }

            if (oneValueIsMissing && oneValueIsPresent) {
                // No need to ask webservice for postal code lookup.
                return of({postalCode: true});
            }

            return getCityFromCountryAndPostalCode(
                control.get('country')?.value,
                control.get('postalCode')?.value,
            ).pipe(
                catchError(error => {
                    if (error instanceof HttpErrorResponse && error.status === 404) {
                        return of(null);
                    } else {
                        throw error;
                    }
                }),
                tap(city => setCity(city)), // Set city in callback
                map(city => (city != null ? null : {postalCode: true})),
            );
        };
    }

    static integer(control: AbstractControl): ValidationErrors | null {
        if (AppValidators.isEmptyInputValue(control.value)) {
            return null; // don't validate empty values to allow optional controls
        }
        const value = String(control.value);
        const regex = /^-?\d+$/;
        return regex.test(value) && !isNaN(Number(value))
            ? null
            : {integer: 'An integer is required'};
    }

    /**
     * A validator that check if a value is within a specific precision and
     * scale. Precision and scale works similarly to that of SQL numbers.
     *
     * If the specified scale is greater than or equal to the precision an
     * error is thrown, indicating this issue.
     *
     * @param precision
     * @param scale
     */
    static preciseNumber(precision: number, scale: number): ValidatorFn {
        if (scale >= precision) {
            throw new Error('Scale cannot be greater than or equal to precision');
        }
        return (control: AbstractControl): ValidationErrors | null => {
            if (AppValidators.isEmptyInputValue(control.value)) {
                return null; // don't validate empty values to allow optional controls
            }

            // If numberCheck returns error, then we don't need to check for precision and scale
            const numberValidationError = this.numberCheck(control);
            if (numberValidationError !== null) {
                return numberValidationError;
            }

            const digits = precision - scale;
            const decimals = scale;

            const value = String(control.value).replace(',', '.');
            const regex = new RegExp(`^-?\\d{1,${digits}}(\\.\\d{0,${decimals}})?$`);
            return regex.test(value)
                ? null
                : {
                      preciseNumber: {
                          message: `Number can have at most ${digits} digits and ${decimals} decimals`,
                          digits,
                          decimals,
                      },
                  };
        };
    }

    static numberCheck(control: AbstractControl): ValidationErrors | null {
        if (AppValidators.isEmptyInputValue(control.value)) {
            return null; // don't validate empty values to allow optional controls
        }
        const value = String(control.value).replace(',', '.');
        const regex = /^-?\d+(\.\d*)?$/;
        return regex.test(value) && !isNaN(Number(value))
            ? null
            : {numberCheck: 'A number is required'};
    }

    static checkAplhabeticString(control: AbstractControl): ValidationErrors | null {
        if (AppValidators.isEmptyInputValue(control.value)) {
            return null; // don't validate empty values to allow optional controls
        }
        const value = String(control.value);
        const regex = /^[a-zA-Z]*$/; // Regular expression to match strings with only letters
        return regex.test(value)
            ? null
            : {checkAplhabeticString: 'A string without numbers is required'};
    }

    // Copied from Angular source code
    public static isEmptyInputValue(value: unknown): boolean {
        /**
         * Check if the object is a string or array before evaluating the length attribute.
         * This avoids falsely rejecting objects that contain a custom length attribute.
         * For example, the object {id: 1, length: 0, width: 0} should not be returned as empty.
         */
        return (
            value == null ||
            ((typeof value === 'string' || Array.isArray(value)) && value.length === 0)
        );
    }

    static minDate(min: DateTime): ValidatorFn {
        return AppValidators.minDateValidator(min);
    }

    private static minDateValidator(min: DateTime): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (
                AppValidators.isEmptyInputValue(control.value) ||
                AppValidators.isEmptyInputValue(min) ||
                !(control.value instanceof DateTime)
            ) {
                return null; // Don't validate empty values to allow optional controls
            }
            const value = control.value as DateTime;
            return value < min ? {minDate: {min: min, actual: value}} : null;
        };
    }

    static maxDate(max: DateTime): ValidatorFn {
        return AppValidators.maxDateValidator(max);
    }

    private static maxDateValidator(max: DateTime): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (
                AppValidators.isEmptyInputValue(control.value) ||
                AppValidators.isEmptyInputValue(max) ||
                !(control.value instanceof DateTime)
            ) {
                return null; // Don't validate empty values to allow optional controls
            }
            const value = control.value as DateTime;
            return value > max ? {maxDate: {max: max, actual: value}} : null;
        };
    }

    static postalCodeExists(
        searchPostalCode: (countryCode: string, postalCode: string) => Observable<string | null>,
    ): AsyncValidatorFn {
        return (
            control: AbstractControl,
        ): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> =>
            searchPostalCode(
                control.get('countryCode')?.value,
                control.get('postalCode')?.value,
            ).pipe(map(it => (it != null && it.length > 0 ? {postalCodeExists: true} : null)));
    }

    static oddMoveDateValidator(control: AbstractControl): ValidationErrors | null {
        if (AppValidators.isEmptyInputValue(control.value)) {
            return null;
        }
        const date = control.value as DateTime;
        if (date.day === 1 || date.day === 15) {
            return null;
        } else {
            return {oddMoveDate: true};
        }
    }

    static sameDateArrayValidator(dates: DateTime[]): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (AppValidators.isEmptyInputValue(control.value)) {
                return null;
            }

            if (typeof control.value === 'string') {
                return null;
            }

            const date: unknown = control.value;
            if (date instanceof DateTime && dates.some(d => d.toISODate() === date.toISODate())) {
                return {sameDate: true};
            } else {
                return null;
            }
        };
    }

    static dateIsFirst(control: AbstractControl): ValidationErrors | null {
        let valid = true;
        const startDate = control.get('startDate')?.value as DateTime | null;
        const endDate = control.get('endDate')?.value as DateTime | null;
        if (
            startDate != null &&
            startDate.isValid &&
            endDate != null &&
            endDate.isValid &&
            startDate !== endDate &&
            startDate.toMillis() > endDate.toMillis()
        ) {
            valid = false;
        }
        return valid ? null : {startDateAfterEndDate: true};
    }

    /**
     * Group validator checking that one date control is on or before another, compared by day.
     * An empty or invalid date passes, so optional controls stay valid.
     * @param error The errors returned when the date is too late, e.g. `{myErrorKey: true}`.
     */
    static dateNotAfter(
        dateControlName: string,
        limitControlName: string,
        error: ValidationErrors,
    ): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const date: unknown = control.get(dateControlName)?.value;
            const limit: unknown = control.get(limitControlName)?.value;
            if (!(date instanceof DateTime) || !(limit instanceof DateTime)) {
                return null;
            }
            if (!date.isValid || !limit.isValid) {
                return null;
            }
            return date.startOf('day') > limit.startOf('day') ? error : null;
        };
    }

    /**
     * Validator to check if a number is between a minimum and maximum value (inclusive).
     * Dynamically returns an error object with a custom error key (e.g., "periodBetween").
     * @param min The minimum value.
     * @param max The maximum value.
     * @param errorKey The dynamic error object key. Default is "numberBetween".
     * @returns A ValidatorFn for Angular forms.
     */
    static numberBetween(
        min: number,
        max: number,
        errorKey: string = 'numberBetween',
    ): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value: unknown = control.value;

            if (value == null) {
                return null;
            }

            const numberValidationError = this.numberCheck(control);

            if (numberValidationError !== null) {
                return numberValidationError;
            }

            const valueAsNumber = Number(value);

            if (isNaN(valueAsNumber) || valueAsNumber < min || valueAsNumber > max) {
                return {
                    [errorKey]: {min, max, actual: valueAsNumber},
                };
            }

            return null;
        };
    }

    static noEmptyStringValidator(
        control: AbstractControl<string | null | undefined>,
    ): ValidationErrors | null {
        const isEmpty = (control.value?.trim() ?? '').length === 0;
        return isEmpty ? {required: true} : null;
    }

    static hexValue(maxValue: number = 255): ValidatorFn {
        return (control: AbstractControl<string>): ValidationErrors | null => {
            if (AppValidators.isEmptyInputValue(control.value)) {
                return null;
            }

            const value = control.value.trim();
            const hexRegex = /^[0-9A-Fa-f]+$/;
            if (!hexRegex.test(value)) {
                return {invalidHex: {value: value}};
            }

            const decimalValue = parseInt(value, 16);
            if (decimalValue > maxValue) {
                return {max: {value: value, max: maxValue.toString(16)}};
            }

            return null;
        };
    }

    static phoneNumber(
        generatePhoneNumber: (countryCallingCode: string, localNumber: string) => string,
    ): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const countryCallingCode: unknown = control.get('countryCallingCode')?.value ?? '';
            const localNumber: unknown = control.get('localNumber')?.value ?? '';

            if (typeof countryCallingCode !== 'string' || typeof localNumber !== 'string') {
                return {invalidPhoneNumber: true};
            }

            const phoneNumber = generatePhoneNumber(countryCallingCode, localNumber);

            if (AppValidators.isEmptyInputValue(phoneNumber)) {
                return null;
            }

            // allowed max. length: 20 characters
            return phoneNumber.length > 20 ? {invalidPhoneNumber: true} : null;
        };
    }
}
